import * as React from 'react';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, Pencil, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { EmptyState } from '@/components/ui/empty-state';
import { RequireAuth } from '@/components/layout/RequireAuth';
import { ClientsKpiTiles, type ClientsKpiTile } from '@/components/clients/kpi-tiles';
import { SectorChip } from '@/components/clients/sector-chip';
import { EditClientDialog } from '@/components/clients/edit-client-dialog';
import { TeamEditor } from '@/components/clients/team-editor';
import { ApiError } from '@/lib/api/client';
import { useClientQuery, useUpdateClientMutation } from '@/lib/api/clients';
import { useEngagementsListQuery } from '@/lib/api/engagements';
import { useMeQuery } from '@/lib/api/engagement-lifecycle';
import { useUserDirectoryQuery } from '@/lib/api/users-directory';
import { useUsersLookupQuery } from '@/lib/api/users-lookup';
import { formatDate } from '@/lib/utils/format';

/**
 * Client detail view (T-0108 / E01-F01 / C20-118).
 *
 * Replaces the v1 scaffold detail (T-0096) — which surfaced only the
 * client metadata + an "engagements pending backend" placeholder — with
 * the prototype-true layout per `design_handoff_caliber_v2/prototype/
 * screens-gaps.js`:
 *
 *   - Header: name + <SectorChip> + back-to-clients link, the Edit client
 *     button (T-0227) and the notes when there are any
 *   - 3-tile KPI strip: engagement count / project count / last activity
 *   - Engagements list (cards linking to `/engagements/:engagementId`)
 *   - Engagement team (`<TeamEditor>`, T-0227): names and firm roles, and
 *     add / remove for firm admins and engagement leads on the team
 *
 * Data sources:
 *   - GET /api/v1/clients/:id         → name, contact, notes, team_member_user_ids, updated_at
 *   - GET /api/v1/engagements?client_id=:id → engagements list (caller-scoped)
 *   - GET /api/v1/me                  → role, to show or hide the edit controls
 *   - GET /api/v1/users/lookup        → member names (any caller)
 *   - GET /api/v1/users/directory     → active users with roles (managers only)
 *   - PATCH /api/v1/clients/:id       → edits and team changes; the server decides
 *
 * Project counts vs. engagement counts
 * ------------------------------------
 * The backend models Projects 1:1 with engagement_ids (T-0022), so
 * "engagement count" and "project count" are the same number today. The
 * prototype surfaces them as separate tiles for forward-compatibility
 * (in case Projects ever fan out into multiple engagements). We render
 * the same number in both tiles and label the second as "Active
 * projects" qualified to the `status='active'` filter, which keeps the
 * dual-count UX live without inventing data.
 */
export const Route = createFileRoute('/clients/$clientId/')({
  component: ClientDetailPage,
});

function ClientDetailPage() {
  const { clientId } = useParams({ from: '/clients/$clientId/' });
  return (
    <RequireAuth>
      <div className="space-y-6 p-card-pad">
        <ClientDetailContent clientId={clientId} />
      </div>
    </RequireAuth>
  );
}

/** Where the server's refusal is shown: its message, or a plain fallback. */
function refusalMessage(e: unknown): string {
  return e instanceof ApiError ? e.message : 'The change could not be saved.';
}

function ClientDetailContent({ clientId }: { clientId: string }) {
  const client = useClientQuery(clientId);
  const engagements = useEngagementsListQuery({ clientId });

  // Firm admins and engagement leads may edit; the server still decides
  // (an engagement lead off the team never sees this page at all).
  const role = useMeQuery().data?.role;
  const canManage = role === 'firm_admin' || role === 'engagement_lead';
  const memberIds = client.data?.team_member_user_ids ?? [];
  const lookup = useUsersLookupQuery(memberIds);
  const directory = useUserDirectoryQuery({ enabled: canManage });
  const update = useUpdateClientMutation(clientId);
  const [editing, setEditing] = React.useState(false);
  const [editError, setEditError] = React.useState<string | null>(null);
  const [teamError, setTeamError] = React.useState<string | null>(null);

  const namesById = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const item of lookup.data?.items ?? []) m.set(item.id, item.display_name);
    return m;
  }, [lookup.data]);

  if (client.isLoading) {
    return (
      <div className="flex items-center gap-2 text-body-sm text-ink-500">
        <Spinner /> Loading client…
      </div>
    );
  }
  if (client.error) return <ErrorMessage error={client.error} />;
  if (!client.data) return <ErrorMessage error={new Error('Client not found.')} />;

  const list = engagements.data?.engagements ?? [];
  const activeProjects = list.filter((e) => e.status === 'active').length;
  const lastActivityIso = client.data.updated_at;

  const tiles: ClientsKpiTile[] = [
    {
      label: 'Engagements',
      value: engagements.isLoading ? '—' : list.length,
      trend: engagements.isLoading
        ? 'Loading…'
        : `${activeProjects} active · ${Math.max(list.length - activeProjects, 0)} other`,
      source: `GET /api/v1/engagements?client_id=${clientId.slice(0, 8)}…`,
      pending: engagements.isLoading,
    },
    {
      label: 'Active projects',
      value: engagements.isLoading ? '—' : activeProjects,
      trend: engagements.isLoading ? 'Loading…' : 'One per engagement',
      source: 'Active engagements',
      pending: engagements.isLoading,
    },
    {
      label: 'Last activity',
      value: lastActivityIso ? formatDate(lastActivityIso) : '—',
      trend: lastActivityIso ? `Updated ${lastActivityIso.slice(0, 10)}` : 'No updates recorded',
      source: 'Client record',
    },
  ];

  return (
    <>
      <div>
        <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2">
          <Link to="/clients">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            <span>Back to clients</span>
          </Link>
        </Button>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-h1 font-semibold text-ink-900">{client.data.name}</h1>
          <SectorChip sector={client.data.client_type} />
          {canManage ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="ml-auto"
              onClick={() => {
                setEditError(null);
                setEditing(true);
              }}
            >
              <Pencil className="h-4 w-4" aria-hidden="true" />
              Edit client
            </Button>
          ) : null}
        </div>
        <p className="mt-1 text-meta text-ink-500">
          Primary contact: {client.data.primary_contact_name} (
          <span className="font-mono">{client.data.primary_contact_email}</span>)
        </p>
        {client.data.notes ? (
          <p className="mt-2 max-w-prose whitespace-pre-line text-body-sm text-ink-700">
            {client.data.notes}
          </p>
        ) : null}
      </div>

      {canManage ? (
        <EditClientDialog
          open={editing}
          onOpenChange={setEditing}
          client={client.data}
          saving={update.isPending}
          error={editError}
          onSave={(changes) => {
            setEditError(null);
            update.mutate(changes, {
              onSuccess: () => setEditing(false),
              onError: (e) => setEditError(refusalMessage(e)),
            });
          }}
        />
      ) : null}

      <ClientsKpiTiles tiles={tiles} />

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle>Engagements</CardTitle>
              <CardDescription>
                Open an engagement to jump into its documents, registers, chat, and archive.
              </CardDescription>
            </div>
            {/*
              New-engagement CTA (T-0117 / C20-127). The client-detail
              entry point preselects this client — the URL-sniffing
              fallback in `EditorModalHost` picks up the clientId from
              the route param, but we also stamp the explicit
              `data-preselect-client-id` so the same trigger works if
              the host is later mounted on a non-route-driven surface.
            */}
            <Button
              type="button"
              data-create="engagement"
              data-preselect-client-id={clientId}
              size="sm"
              className="bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              New engagement
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {engagements.isLoading ? (
            <div className="flex items-center gap-2 text-body-sm text-ink-500">
              <Spinner /> Loading engagements…
            </div>
          ) : engagements.error ? (
            <ErrorMessage error={engagements.error} />
          ) : list.length === 0 ? (
            <EmptyState
              title="No engagements"
              description="This client doesn't have any engagements yet. A firm admin can create one from the project creation flow."
            />
          ) : (
            <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((eng) => (
                <li key={eng.id}>
                  <Link
                    to="/engagements/$engagementId"
                    params={{ engagementId: eng.id }}
                    className="block rounded-card border border-ink-100 bg-paper-0 p-4 transition-shadow hover:shadow-modal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate text-body-sm font-semibold text-ink-900">
                          {eng.name}
                        </div>
                        <div className="mt-1 truncate font-mono text-[11px] text-ink-500">
                          {eng.id}
                        </div>
                      </div>
                      <span
                        className={
                          eng.status === 'active'
                            ? 'inline-flex h-[22px] items-center whitespace-nowrap rounded-full bg-posture-healthy-tint px-2.5 text-meta font-medium text-posture-healthy'
                            : 'inline-flex h-[22px] items-center whitespace-nowrap rounded-full bg-ink-100 px-2.5 text-meta font-medium text-ink-700'
                        }
                      >
                        {eng.status}
                      </span>
                    </div>
                    <div className="mt-3 text-[11px] text-ink-500">
                      Modules:{' '}
                      {eng.modules_enabled.length === 0 ? '—' : eng.modules_enabled.join(' · ')}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Engagement team</CardTitle>
          <CardDescription>
            Firm staff assigned to this client, with their firm role. Engagement leads on the team
            and firm admins manage the client and its engagements.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <TeamEditor
            memberIds={memberIds}
            namesById={namesById}
            directory={canManage ? directory.data : undefined}
            canManage={canManage}
            saving={update.isPending}
            error={teamError ?? (directory.error ? refusalMessage(directory.error) : null)}
            onAdd={(userId) => {
              setTeamError(null);
              update.mutate(
                { add_team_member_user_ids: [userId] },
                { onError: (e) => setTeamError(refusalMessage(e)) },
              );
            }}
            onRemove={(userId) => {
              setTeamError(null);
              update.mutate(
                { remove_team_member_user_ids: [userId] },
                { onError: (e) => setTeamError(refusalMessage(e)) },
              );
            }}
          />
        </CardContent>
      </Card>
    </>
  );
}
